import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import StudentLayout from "@/components/StudentLayout";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Sunrise, Sun } from "lucide-react";
import { toast } from "sonner";
import { to12h } from "@/lib/time";

const inferBlock = (p) => {
  if (p.block === "AM" || p.block === "PM") return p.block;
  const hh = parseInt((p.start_time || "0").split(":")[0], 10);
  return Number.isFinite(hh) && hh < 13 ? "AM" : "PM";
};

const toMin = (hm) => {
  const [h, m] = (hm || "").split(":").map((x) => parseInt(x, 10));
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function StudentTimetable() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nowMin, setNowMin] = useState(() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); });

  // Tick every 30s so the "running now" highlight moves through the day live.
  useEffect(() => {
    const id = setInterval(() => { const d = new Date(); setNowMin(d.getHours() * 60 + d.getMinutes()); }, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/student/timetable", { params: { date } });
        if (!cancelled) setPeriods((data.periods || []).map((p) => ({ ...p, block: inferBlock(p) })));
      } catch (e) {
        if (!cancelled) toast.error(apiErr(e.response?.data?.detail) || "Could not load timetable");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [date]);

  const am = periods.filter((p) => p.block === "AM");
  const pm = periods.filter((p) => p.block === "PM");

  // A period is "running" only when viewing today and now falls within it.
  const isRunning = (p) => {
    if (date !== todayISO()) return false;
    const s = toMin(p.start_time), e = toMin(p.end_time);
    return s != null && e != null && nowMin >= s && nowMin < e;
  };

  return (
    <StudentLayout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">My timetable</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>What I'm learning today</h1>
        </div>
        <DatePicker value={date} onChange={setDate} className="h-11 rounded-xl w-48" data-testid="student-tt-date"/>
      </div>

      {loading ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500">Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <BlockCard tone="sky" icon={Sunrise} title="Morning" rows={am} testid="student-tt-am" isRunning={isRunning}/>
          <BlockCard tone="amber" icon={Sun} title="Afternoon" rows={pm} testid="student-tt-pm" isRunning={isRunning}/>
        </div>
      )}
    </StudentLayout>
  );
}

function BlockCard({ tone, icon: Icon, title, rows, testid, isRunning }) {
  const tones = {
    sky: { ring: "border-sky-200 dark:border-sky-800", bg: "bg-sky-50 dark:bg-sky-950/40", chip: "bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300" },
    amber: { ring: "border-amber-200 dark:border-amber-800", bg: "bg-amber-50 dark:bg-amber-950/30", chip: "bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300" },
  };
  const t = tones[tone];
  return (
    <div className={`bg-white dark:bg-slate-800 rounded-2xl border ${t.ring} overflow-hidden`} data-testid={testid}>
      <div className={`px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center gap-3 ${t.bg}`}>
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${t.chip}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>{title}</div>
          <div className="text-xs text-slate-500">{rows.length} period{rows.length === 1 ? "" : "s"}</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm">No {title.toLowerCase()} periods scheduled.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-700 text-slate-500 text-xs uppercase tracking-[0.15em]">
            <tr>
              <th className="px-3 py-3 text-left w-10">#</th>
              <th className="px-3 py-3 text-left">Subject</th>
              <th className="px-3 py-3 text-left">Teacher</th>
              <th className="px-3 py-3 text-left w-24">Start</th>
              <th className="px-3 py-3 text-left w-24">End</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {rows.map((p, i) => {
              const live = isRunning?.(p);
              return (
                <tr key={`${title}-${i}`} className={live ? "tt-live-row" : ""} data-testid={live ? `${testid}-live-row` : undefined}>
                  <td className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{p.period}</td>
                  <td className="px-3 py-3 font-medium text-slate-900 dark:text-slate-100">
                    <span className="inline-flex items-center gap-2">
                      {p.subject}
                      {live && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5" data-testid={`${testid}-live-badge`}>
                          <span className="tt-live-dot h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-slate-600 dark:text-slate-300">{p.teacher}</td>
                  <td className="px-3 py-3 font-mono text-slate-600 dark:text-slate-300">{to12h(p.start_time)}</td>
                  <td className="px-3 py-3 font-mono text-slate-600 dark:text-slate-300">{to12h(p.end_time)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
